import { ref, computed, onActivated, watchEffect } from 'vue';
import { useRoute } from 'vue-router';
import { get } from '~/http';
import { useDeviceStore } from '~/store/device';
import { useMenusStore } from '~/store/menus';
import { useHomePageStore } from '~/store/homePage';
import { useMenuMatch } from '~/composables/useMenuMatch';
import { useI18n } from 'vue-i18n';
import { stripLocalePrefix } from '~/utils/localeUrl';

/**
 * 页面数据加载和 SEO 设置的 composable
 * @param {Object} options - 配置选项
 * @param {string|null} options.pagePath - 页面路径，如果为 null 则从路由参数中获取，如果为 false 则获取首页数据
 * @param {boolean} options.enableKeepalive - 是否启用 keepalive（默认 false）
 * @param {string} options.logPrefix - 日志前缀，用于区分不同页面
 * @returns {Object} 返回数据和方法
 */
export const usePageData = (options = {}) => {
    const {
        pagePath = undefined,
        enableKeepalive = false,
        logPrefix = '页面'
    } = options;

    const route = useRoute();
    const data = ref(null);
    
    const deviceStore = useDeviceStore();
    const menusStore = useMenusStore();
    const homePageStore = useHomePageStore();
    const { currentRoutePath, checkMenuMatch } = useMenuMatch();
    const { locale } = useI18n();

    const getSeo = (responseData) => {
        if (!responseData) {
            return {
                title: homePageStore.seoTitle,
                description: homePageStore.seoDescription,
                keywords: homePageStore.seoKeywords
            };
        }

        const content = responseData._parsedContent || JSON.parse(responseData.content || '{}');
        const pageSetting = content.pageSetting || {};
        return {
            title: pageSetting.title || responseData.title || responseData.pageName || '',
            description: pageSetting.description || responseData.description || '',
            keywords: pageSetting.keywords || responseData.keywords || pageSetting.keyword || responseData.keyword || ''
        };
    };

    const seo = computed(() => getSeo(data.value));

    // 自动从路由中获取 pagePath（如果未提供）
    const getPagePath = () => {
        if (pagePath === false) return null;
        if (pagePath !== undefined) return pagePath;

        // 剥除 locale 路径前缀后，若路径为根则是该语言的首页，不传 pagePath。
        // 例：Case 1 英文首页 /en → stripped '/' → null
        //     Case 2 中文首页 /zh_cn → stripped '/' → null
        const stripped = stripLocalePrefix(route.path, homePageStore.webConfig, locale.value);
        if (stripped === '/') return null;

        // 其余情况使用路由参数（多段路径中同名 [id] 参数只保留最后一段，即真实页面 id）
        return route.params.id || null;
    };

    // 设置 SEO（在 setup 顶层调用，确保在正确的上下文中）
    useHead({
        title: computed(() => seo.value.title || undefined),
        meta: computed(() => {
            const meta = [];
            if (seo.value.description) {
                meta.push({
                    name: 'description',
                    content: seo.value.description
                });
            }
            if (seo.value.keywords) {
                meta.push({
                    name: 'keywords',
                    content: seo.value.keywords
                });
            }
            return meta;
        })
    });

    // 加载页面数据
    const loadData = async () => {
        const currentPagePath = getPagePath();
        const params = currentPagePath !== null ? { pagePath: currentPagePath } : {};
        console.log('params', params)
        
        const { data: responseData } = await get('/app/web_page_config/web_config_path', params);

        // debugger;

        // 页面数据不存在，返回 404
        if (!responseData) {
            throw createError({ statusCode: 404, message: 'Page not found' });
        }

        const content = JSON.parse(responseData.content || '{}');
        console.log(`页面配置 ${logPrefix} _数据_ config`, responseData);
        console.log(`页面配置 ${logPrefix} _数据_ content`, content);
        console.log(`页面配置 ${logPrefix} _数据_ componentSetting`, content.componentSetting);
        
        // 设置菜单相关状态
        if (responseData.pagePath) {
            menusStore.setUrlId(responseData.pagePath);
        }
        if (responseData.level !== undefined) {
            let finalLevel = responseData.level;
            
            // 如果 level > 0 且 < 6，从网址获取 level 并取较大值
          // 为了兼容 https://hibaacademy.retailconnection.cn/admissions/contact-us 这种 contact-us 实际是个1级页面
            if (responseData.level > 0 && responseData.level < 6) {
                // 从当前路由路径计算 level（路径段数）
                const normalizedPath = currentRoutePath.value;
                // 移除开头的 /，然后按 / 分割，过滤空字符串
                const pathSegments = normalizedPath.replace(/^\/+/, '').split('/').filter(Boolean);
                // level 等于路径段数（例如 /admissions/contact-us -> 2）
                const urlLevel = pathSegments.length;
                
                // 取较大的 level
                finalLevel = Math.max(responseData.level, urlLevel);
            }
            
            menusStore.setMenuLevel(finalLevel);
        }

        // 设置页面设置（包含 SEO 信息）
        const pageSetting = content.pageSetting || {};
        const componentSetting = content.componentSetting || [];
        homePageStore.setPageSetting(pageSetting, responseData);

        // 将解析后的 content 对象保存到 responseData 中，以便后续修改
        // 这样 desktop/index.vue 中解析时使用的是同一个对象引用
        responseData._parsedContent = content;
        data.value = responseData;

        return responseData;
    };

    // 根据当前匹配的菜单更新 banner 标题
    watchEffect(() => {
        if (!data.value || !data.value._parsedContent) return;
        if (!menusStore.menus || menusStore.menus.length === 0) return;
        
        const componentSetting = data.value._parsedContent.componentSetting || [];
        const bannerItem = componentSetting.find(item => 7 === item.type);
        if (!bannerItem) return;
        
        let fn = (list, parent) => {
            for (let i = 0; i < list.length; i++) {
                let obj = list[i];
                // 使用精准匹配逻辑
                if (checkMenuMatch(obj)) {
                    if (parent) {
                        if ('zh' === locale.value) {
                            bannerItem.zhTitle = parent.label;
                            bannerItem.enTitle = '';
                        } else {
                            bannerItem.zhTitle = '';
                            bannerItem.enTitle = parent.label;
                        }
                    }
                    return;
                } else {
                    if (obj.child && obj.child.length) {
                        fn(obj.child, obj);
                    }
                }
            }
        }
        fn(menusStore.menus, null);
    });

    const initData = async () => {
        await loadData();
    };

    // 如果启用 keepalive，在页面激活时重新加载数据
    if (enableKeepalive) {
        onActivated(async () => {
            await loadData();
        });
    }

    return {
        data,
        deviceStore,
        loadData,
        initData
    };
};
